import { useCallback } from 'react'
import { parseUnits } from '@ethersproject/units'
import { unstakeFarm } from 'utils/calls'
import { useMasterchef, useSousChef } from 'hooks/useContract'
import getGasPrice from 'utils/getGasPrice'
import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'utils/bigNumber'

let options = {
  gasLimit: 700000,
}

const sousUnstake = async (sousChefContract: any, amount: string, decimals: number, isNft?: boolean, pool?: any) => {
  const gasPrice = getGasPrice()
  const units = parseUnits(amount, decimals)
  const fee =
    pool?.withdrawFee && !pool?.withdrawFeeIsZero
      ? new BigNumber((await sousChefContract.getWithdrawFee()).toString())
          .times(1.02)
          .integerValue(BigNumber.ROUND_DOWN)
          .toString()
      : undefined
  const estimate = Number(
    ((await sousChefContract.estimateGas.withdraw(units.toString(), { ...(fee ? { value: fee } : {}) })) * 1.1).toFixed(
      0,
    ),
  )
  return sousChefContract.withdraw(units.toString(), {
    ...(fee ? { value: fee } : {}),
    gasPrice,
    gasLimit: estimate,
  })
}

const sousUnstakeNFTpools = async (
  sousChefContract: any,
  amount: string,
  decimals: number,
  isNft?: boolean,
  pool?: any,
  rewards?: number,
) => {
  const gasPrice = getGasPrice()
  const units = parseUnits(amount, decimals)
  const mintNFTs = Math.floor(rewards / 10 ** 18)
  const fee = pool?.withdrawFee
    ? new BigNumber((await sousChefContract.getWithdrawFee()).toString()).times(Number(mintNFTs))
    : BIG_ZERO
  options.gasLimit = Number(
    (
      (await sousChefContract.estimateGas.withdraw(units.toString(), {
        value: fee.times(1.02).integerValue(BigNumber.ROUND_DOWN).toString(),
      })) * 1.1
    ).toFixed(0),
  )

  return sousChefContract.withdraw(units.toString(), {
    value: fee.times(1.02).integerValue(BigNumber.ROUND_DOWN).toString(),
    gasPrice,
    ...options,
  })
}

const sousEmergencyUnstake = async (sousChefContract: any, pool?: any) => {
  const gasPrice = getGasPrice()

  const fee =
    pool?.withdrawFee && !pool?.withdrawFeeIsZero
      ? new BigNumber((await sousChefContract.getWithdrawFee()).toString())
          .times(1.02)
          .integerValue(BigNumber.ROUND_DOWN)
      : new BigNumber(0)

  const estimate = await sousChefContract.estimateGas.emergencyWithdraw({ value: fee.toString() })

  return sousChefContract.emergencyWithdraw({
    value: fee.toString(),
    gasLimit: (estimate * 1.1).toFixed(0),
    gasPrice,
  })
}

const useUnstakePool = (sousId: number, enableEmergencyWithdraw = false) => {
  const masterChefContract = useMasterchef()
  const sousChefContract = useSousChef(sousId)

  const handleUnstake = useCallback(
    async (amount: string, decimals: number, isNft?: boolean, pool?: any, rewards?: number) => {
      if (sousId === 0) {
        return unstakeFarm(masterChefContract, 0, amount, 18)
      }
      if (enableEmergencyWithdraw) {
        return sousEmergencyUnstake(sousChefContract)
      }

      if (sousId === 41 || sousId === 42 || pool.isNft) {
        return sousUnstakeNFTpools(sousChefContract, amount, decimals, isNft, pool, rewards)
      }
      return sousUnstake(sousChefContract, amount, decimals, isNft, pool)
    },
    [enableEmergencyWithdraw, masterChefContract, sousChefContract, sousId],
  )

  return { onUnstake: handleUnstake }
}

export default useUnstakePool
